/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : PendingRequestsTab.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */


/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 23-09-2024
 * @description : Shows list of pending requests which are to be approved
 */

import {
  Button,
  Typography,
  Divider,
  Grid,
  Box,
  IconButton,
  ToggleButton,
  Tooltip,
  DialogContentText,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  CustomTextFieldN,
} from "../../../components";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { HandleApiActions, userManagementApis } from "../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import customConsole from "../../../config/customConsole";
import helper from "../../../utils/helper";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import appTheme from "../../../assets/AppTheme/appTheme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomRadioButtonV3 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV3";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomFilterTemplate from "../../../components/customComponentsV2/common/filter/CustomFilterTemplate";
import SortTemplate from "../../../components/customComponentsV2/common/Templates/SortTemplate";
import FilterTemplate from "../../../components/customComponentsV2/common/Templates/FilterTemplate";

function PendingRequestsTab({
  moduleTypes,
  selectedProjectDetails,
  accessModuleTypes,
  accessRequestTypes,
  accessRequestStatus,
}) {
  const dispatch = useDispatch();

  // Redux store
  const profileDetails = useSelector((store) => store.profileReducer);

  // request access data
  const [requestAccessList, setRequestAccessList] = useState([]);

  // Table rows data
  const [tableRowsData, setTableRowsData] = useState([]);

  // Is request send
  const [isRequestSend, setIsRequestSend] = useState(true);

  // Reason to reject access
  const [reasonToRejectAccess, setReasonToRejectAccess] = useState("");
  //Reason to forward access
  const [reasonToForwardAccess, setReasonToForwardAccess] = useState("");
  const [forwardApprovalPayload, setForwardApprovalPayload] = useState({});

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [hasMore, setHasMore] = useState(false);

  // Filtering
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedSearchField, setSelectedSearchField] = useState("access_type");
  const [searchFieldValue, setSearchFieldValue] = useState("");

  // Dialogs states
  const [openApproveAccessDialog, setOpenApproveAccessDialog] = useState(false);
  const [openForwardApproveAccessDialog, setOpenForwardApproveAccessDialog] =
    useState(false);
  const [selectedApproveAccessData, setSelectedApproveAccessData] =
    useState(null);
  const [openRejectAccessDialog, setOpenRejectAccessDialog] = useState(false);
  const [selectedRejectAccessData, setSelectedRejectAccessData] =
    useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [projectRequestCount, setProjectRequestCount] = useState("");




  useEffect(() => {
    console.log("selectedApproveAccessData =", selectedApproveAccessData);
  }, [selectedApproveAccessData]);

  // Handle approve access stages
  const handleApproveAccessClick = (params) => {
    console.log("params =", params);
    setSelectedApproveAccessData({
      id: params?.id,
      approvingAccess: params?.requested_access,
    });

    setSelectedRowData(params);

    setOpenApproveAccessDialog(true);
  };
  const handleConfirmApproveAccess = (canOpenApproveAccessDialog = false) => {
    approveRequestAccess(selectedApproveAccessData);

    setTimeout(() => {
      getRequestAccessList({
        projectId: selectedProjectDetails?.project_id,
        pageNumber: page,
        pageSize: rowsPerPage,
      });
    }, 500); // 500ms delay

    setSelectedApproveAccessData(null);

    setOpenApproveAccessDialog(canOpenApproveAccessDialog);
  };

  //Handle Cancel Approve Access
  const handleCancelApproveAccess = () => {
    setSelectedApproveAccessData(null);
    setOpenApproveAccessDialog(false);
  };



  const handleForwardAccessClick = async (params) => {
    console.log("---forwardApprovalPayload---", forwardApprovalPayload);
    let approveResponse = await dispatch(
      HandleApiActions(forwardApprovalPayload),
    );
    console.log("---approveResponse---", approveResponse);
    ForwardRequestAccess();
    setTimeout(() => {
      getRequestAccessList({
        projectId: selectedProjectDetails?.project_id,
        pageNumber: page,
        pageSize: rowsPerPage,
      });
    }, 500); // 500ms delay
    setOpenForwardApproveAccessDialog(false);
    setOpenApproveAccessDialog(false);
    setReasonToForwardAccess("");
  };

  //Handle Forward Approve Access
  const ForwardRequestAccess = async () => {
    let apiDetails = userManagementApis.forward_access;

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          access_request_id: Number(selectedRowData?.id),
          feature_ids: selectedRowData?.requested_feature_access_details?.map(
            (feature) => feature?.id,
          ),
          page_ids: selectedRowData?.requested_page_access_details?.map(
            (page) => page?.id,
          ),
          forward_reason: reasonToForwardAccess,
          requested_for: selectedRowData?.requested_user?.user_id,
        },
        show_toast: true,
      }),
    );
  };

  //Handle Cancel Forward access
  const handleCancelForwardAccess = () => {
    setOpenForwardApproveAccessDialog(false);
    setReasonToForwardAccess("");
  };

  // Handle reject access stages
  const handleRejectAccessClick = (params) => {
    setSelectedRejectAccessData(params);

    setOpenRejectAccessDialog(true);

    setReasonToRejectAccess("");
  };
  const handleConfirmRejectAccess = () => {
    rejectRequestAccess({
      ...selectedRejectAccessData,
      reason: reasonToRejectAccess,
    });

    setTimeout(() => {
      getRequestAccessList({
        projectId: selectedProjectDetails?.project_id,
        pageNumber: page,
        pageSize: rowsPerPage,
      });
    }, 500); // 500ms delay

    setSelectedRejectAccessData(null);

    setOpenRejectAccessDialog(false);
  };
  const handleCancelRejectAccess = () => {
    setSelectedRejectAccessData(null);

    setOpenRejectAccessDialog(false);

    setReasonToRejectAccess("");
  };

   const [selectedSortObjBeforeClick, setSelectedSortObjBeforeClick] = useState({
      sortBy: "created_at",
      sortOrder: "DESC",
   });
     const [selectedSortObjAfterClick, setSelectedSortObjAfterClick] = useState({
      sortBy: "created_at",
      sortOrder: "DESC",
     });
    const [
      selectedFilterByStatusObjBeforeClick,
      setSelectedFilterByStatusObjBeforeClick,
    ] = useState([]);

      const [
      selectedFilterByStatusObjAfterClick,
      setSelectedFilterByStatusObjAfterClick,
    ] = useState([]);

  const [
      selectedFilterByModuleObjBeforeClick,
      setSelectedFilterByModuleObjBeforeClick,
    ] = useState([]);

      const [
      selectedFilterByModuleObjAfterClick,
      setSelectedFilterByModuleObjAfterClick,
    ] = useState([]);
  const getRequestAccessList = async ({ projectId, pageNumber, pageSize }) => {
    let apiDetails = userManagementApis.get_request_access_list;

    customConsole({
      ...apiDetails,
      params: {
        is_sent_requests: isRequestSend,
        project_id: projectId,
        page_number: pageNumber,
        page_size: pageSize,
        ...(searchFieldValue && { [selectedSearchField]: searchFieldValue }),
      },
    });

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          is_sent_requests: isRequestSend,
          project_id: projectId,
          page_number: pageNumber,
          page_size: pageSize,
          // ...(searchFieldValue && { [selectedSearchField]: searchFieldValue }),
          status: selectedFilterByStatusObjAfterClick.map(data => data.value).join(","),
          modules: selectedFilterByModuleObjAfterClick.map(data => data.value).join(","),
            order_by_sort: selectedSortObjAfterClick.sortOrder,
                order_by_column: selectedSortObjAfterClick.sortBy,
        },
      }),
    );

    if (resp.error) {
      setRequestAccessList([]);
    } else {
      setRequestAccessList(resp.data);
      setProjectRequestCount(resp?.total_count)
    }
  };

  console.log("RequestAccessList", requestAccessList);

  const approveRequestAccess = async ({
    id,
    approvingAccess,
    selectedProjectId,
    page,
    rowsPerPage,
  }) => {
    console.log("id =", id);
    console.log("approvingAccess =", approvingAccess);
    let apiDetails = userManagementApis.approve_access;

    customConsole({
      ...apiDetails,
      params: {
        id: Number(id),
        approving_access: approvingAccess,
      },
      show_toast: true,
    });

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          id: Number(id),
          approving_access: approvingAccess,
        },
        show_toast: true,
      }),
    );

    if (!resp.error) {
      setOpenApproveAccessDialog(false);
    }
  };

  const rejectRequestAccess = async ({
    id,
    reason,
    selectedProjectId,
    page,
    rowsPerPage,
  }) => {
    let apiDetails = userManagementApis.reject_access;

    customConsole({
      ...apiDetails,
      params: {
        id: Number(id),
        reason: reason,
      },
      show_toast: true,
    });

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          id: Number(id),
          reason: reason,
        },
        show_toast: true,
      }),
    );
  };


  const COLUMNS = [
    {
      field: "sNo",
      headerName: "S.No",
      flex: 0.3,
      minWidth: 60,
      filterable: false,
      sortable: false,
    },
    {
      field: "requested_user",
      headerName: "Requested By",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          <Typography variant="subtitle2">
            <span style={styles.txtBold}>Name: </span>
            {params?.row?.requested_user?.fullname || "-"}
          </Typography>
          <Typography variant="subtitle2">
            <span style={styles.txtBold}>Email: </span>
            {params?.row?.requested_user?.email || "-"}
          </Typography>
          {/* <Typography variant="subtitle2">
            <span style={styles.txtBold}>Ownership: </span>
            {params?.row?.requested_user?.ownership || "-"}
          </Typography> */}
        </Box>
      ),
    },
    {
      field: "module_type_name",
      headerName: "Module Type",
      flex: 0.5,
      minWidth: 80,
    },
    {
      field: "module_details",
      headerName: "Module Details",
      flex: 0.9,
      minWidth: 140,
      renderCell: (params) =>
        params?.row?.module_type_name == "PROJECT" ? (
          <Box>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Name: </span>
              {params?.row?.project_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Architecture Name: </span>
              {params?.row?.module_details?.project_details
                ?.architecture_name || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Trade Mark: </span>
              {params?.row?.module_details?.project_details?.trade_mark || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Delivery Market: </span>
              {params?.row?.module_details?.project_details?.delivery_market ||
                "-"}
            </Typography>
          </Box>
        ) : params?.row?.module_type_name == "VARIANT" ? (
          <Box>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Name: </span>
              {params?.row?.module_details?.variant_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Manufacture: </span>
              {params?.row?.module_details?.variant_details?.mft_details
                ?.name || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Delivery Market: </span>
              {params?.row?.module_details?.variant_details?.delivery_market ||
                "-"}
            </Typography>
          </Box>
        ) : params?.row?.module_type_name == "VC" ? (
          <Box>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Name: </span>
              {params?.row?.module_details?.vehicle_details?.component || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Assembly Number: </span>
              {params?.row?.module_details?.vehicle_details?.assembly_number ||
                "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Summary: </span>
              {params?.row?.module_details?.vehicle_details?.summary || "-"}
            </Typography>
          </Box>
        ) : params?.row?.module_type_name == "ZONE" ? (
          <Box>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Name: </span>
              {params?.row?.module_details?.zone_details?.zone?.zone_name ||
                "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Position: </span>
              {params?.row?.module_details?.zone_details?.zone?.position || "-"}
            </Typography>
          </Box>
        ) : params?.row?.module_type_name == "EE" ? (
          <Box>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Description: </span>
              {params?.row?.module_details?.ee_details?.description || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Version: </span>
              {params?.row?.module_details?.ee_details?.version || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Supplier Name: </span>
              {params?.row?.module_details?.ee_details?.supplier_name || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Assembly Number: </span>
              {params?.row?.module_details?.ee_details?.assembly_number || "-"}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Name: </span>
              {params?.row?.module_details?.swm_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Version: </span>
              {params?.row?.module_details?.swm_details?.version || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Description: </span>
              {params?.row?.module_details?.swm_details?.description || "-"}
            </Typography>
          </Box>
        ),
    },
    {
      field: "access_type",
      headerName: "Access Type",
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="subtitle2">
            {params?.row?.requested_access_name}
          </Typography>
          {/* <IconButton
            size="small"
            color="primary"
            sx={{ marginLeft: 0.4 }}
            onClick={() => {}}
          >
            <EditIcon fontSize="small" />
          </IconButton> */}
        </Box>
      ),
    },
    {
      field: "request_reason",
      headerName: "Reason",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "created_at",
      headerName: "Requested At",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "approved_user",
      headerName: "Approved By",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        // If status is not pending(100), show approved user details
        <Box>
          <Typography variant="subtitle2">
            <span style={styles.txtBold}>Name: </span>
            {params?.row?.status !== 100
              ? params?.row?.approved_user?.fullname
              : "-"}
          </Typography>
          <Typography variant="subtitle2">
            <span style={styles.txtBold}>Email: </span>
            {params?.row?.status !== 100
              ? params?.row?.approved_user?.email
              : "-"}
          </Typography>
        </Box>
      ),
    },
    // ...(isRequestSend
    //   ? [
    //       {
    //         field: "status_data",
    //         headerName: "Status",
    //         width: 220,
    //       },
    //     ]
    //   : []),
    {
      field: "status_data",
      headerName: "Status",
      flex: 0.6,
      minWidth: 100,
    },
    ...(!isRequestSend
      ? [
          {
            field: "action",
            headerName: "Action",
            flex: 0.6,
            minWidth: 100,
            renderCell: (params) =>
              params?.row?.status === 100 ? (
                <Box
                >
                  {/* <Box sx={{ display: "flex", alignItems: "center" }}> */}
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() =>
                      handleApproveAccessClick(
                        params.row,
                        // id: params?.row?.id,
                        // approvingAccess: params?.row?.requested_access,
                      )
                    }
                  >

                    <CheckCircleIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      handleRejectAccessClick({
                        id: params?.row?.id,
                      })
                    }
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>
              ) : (
                "-"
              ),
          },
        ]
      : []),
  ];

  // Open filter menu


  // Handle filter close
  const handleFilterClose = () => {
    setAnchorElFilter(null);
    setOpenFilter(false);
  };

  // Handle filter change
  const handleFilterChange = (searchField, searchValue) => {
    setSelectedSearchField(searchField);
    setSearchFieldValue(searchValue);

    setPage(1); // Reset to the first page when page size changes

    handleFilterClose();
  };


  /* UseEffects */
  useEffect(() => {
    // Get the request access list
    if (selectedProjectDetails?.project_id) {
      getRequestAccessList({
        projectId: selectedProjectDetails?.project_id,
        pageNumber: page,
        pageSize: rowsPerPage,
      });
    }
  }, [
    selectedProjectDetails,
    page,
    rowsPerPage,
    selectedSearchField,
    searchFieldValue,
    isRequestSend,
    selectedFilterByStatusObjAfterClick,
    selectedFilterByModuleObjAfterClick,
    selectedSortObjAfterClick
  ]);

  useEffect(() => {
    // Handle pagination hasMore
    if (requestAccessList?.length < rowsPerPage) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
    console.log("requestAccessListrequestAccessListrequestAccessList",requestAccessList)
    let filteredRequestAccessList = requestAccessList
      // ?.filter((el) => {
      //   // If is request send and the status is not pending(100)
      //   if (!isRequestSend && el?.status !== 100) {
      //     return false;
      //   }
      //   return true;
      // })
      ?.map((rc, index) => {
        return {
          ...rc,
          sNo: (page - 1) * rowsPerPage + index + 1,
          requested_user: rc?.requested_user?.length && rc?.requested_user[0],
          module_type: rc?.module_type,
          module_type_name: rc?.module_type_name,
          module_details: {
            project_details:
              rc?.project_details?.length && rc?.project_details?.[0],
            variant_details:
              rc?.variant_details?.length && rc?.variant_details?.[0],
            vehicle_details:
              rc?.vehicle_component_details?.length &&
              rc?.vehicle_component_details?.[0],
            zone_details: rc?.zone_details?.length && rc?.zone_details?.[0],
            ee_details: rc?.ee_details?.length && rc?.ee_details?.[0],
            swm_details: rc?.swm_details?.length && rc?.swm_details?.[0],
          },
          access_type:
            helper?.getValueByKey(accessRequestTypes, rc?.requested_access) ||
            "-",
          access_type_name: rc?.requested_access_name,
          created_at: convertUtcToTimeZone(
            rc.created_at,
            profileDetails?.profile?.profile?.region,
          ),
          approved_user: rc?.approved_user?.length && rc?.approved_user[0],
          status_data:
            helper?.getValueByKey(accessRequestStatus, rc?.status) || "-",
        };
      });
    setTableRowsData(filteredRequestAccessList);
  }, [requestAccessList]);

  customConsole("selectedApproveAccessData");
  customConsole(selectedApproveAccessData);

  customConsole("selectedRowData");
  customConsole(selectedRowData);

  return (
    <Box sx={{ mb: 12, width: "100%", overflowX: "hidden" }}>
      <Box sx={styles.filterBtnContainer}>
        {/* <CustomFiltersN
          anchorEl={anchorElFilter}
          open={openFilter}
          handleClose={handleFilterClose}
          mainOptionsList={[
            {
              value: "access_type",
              label: "Access Type",
              type: "select",
              value_options_list:
                helper?.convertKeyValueToLabel(accessRequestTypes) || [],
            },
            {
              value: "module_type",
              label: "Module Type",
              type: "select",
              value_options_list:
                helper?.convertKeyValueToLabelFiltered(accessModuleTypes, [
                  100,
                  ...(selectedProjectDetails?.is_zonal ? [] : [200]),
                ]) || [],
            },
            {
              value: "status",
              label: "Status",
              type: "select",
              value_options_list:
                helper?.convertKeyValueToLabel(accessRequestStatus) || [],
            },
          ]}
          selectedMainOption={selectedSearchField}
          selectedMainOptionValue={searchFieldValue}
          handleFilterClick={handleFilterClick}
          onReset={handleFilterResetAll}
          onApplyNow={handleFilterApplyNow}
        /> */}
          <CustomFilterTemplate
                  handleDiscard={() => {
                    setSelectedSortObjBeforeClick({
                      sortBy: "created_at",
                      sortOrder: "DESC",
                    });
                  }}
                  handleClickApply={() => {
                    //  fetchUserDetails(page,rowsPerPage,selectedSortObjBeforeClick?.sortBy,selectedSortObjBeforeClick.sortOrder)
                    setSelectedSortObjAfterClick(selectedSortObjBeforeClick)
                              setSelectedFilterByStatusObjAfterClick(selectedFilterByStatusObjBeforeClick)
                    setSelectedFilterByModuleObjAfterClick(selectedFilterByModuleObjBeforeClick)
                  }}

                  sections={[
                    {
                      heading: "Sorting",
                      content: (
                        <SortTemplate
                          defaultValue={selectedSortObjBeforeClick}
                          handleOnChange={(sortValue) => {
                            setSelectedSortObjBeforeClick(sortValue);
                          }}
                        />
                      ),
                    },
                    {
                      heading: "Filter By Status",
                      content: (
                        <FilterTemplate
                          defaultValue={selectedFilterByStatusObjBeforeClick}
                          handleOnChange={(sortValue) => {
                            setSelectedFilterByStatusObjBeforeClick(sortValue);
                          }}
                          
                      data={[{label:"Approved",value:100},{label:"Rejected",value:200},{label:"Pending",value:300}]}
                        />
                        
                       
                      ),
                    },
                     {
                      heading: "Filter By Module",
                      content: (
                        <FilterTemplate
                          defaultValue={selectedFilterByModuleObjBeforeClick}
                          handleOnChange={(sortValue) => {
                            setSelectedFilterByModuleObjBeforeClick(sortValue);
                          }}
                           data={[{label:"Project",value:100},{label:"Variant",value:200},{label:"EE Component",value:300},{label:"Software Module",value:300},{label:"Vehicle System",value:300},{label:"Vehicle Component",value:200}]}
                        />
                        
                       
                      ),
                    },
                   
                   
                   
                  ]}
                />
        <ToggleButton
          value="check"
          selected={isRequestSend}
          onChange={() => setIsRequestSend(!isRequestSend)}
          style={{
            width: "25px",
            height: "25px",
            backgroundColor: isRequestSend ? "#4CAF50" : "#F44336",
            color: "white",
          }}
        >
          {isRequestSend ? (
            <Tooltip title={"Sent Access Request"}>
              <CallMadeIcon sx={{ backgroundColor: "#26890D" }} />
            </Tooltip>
          ) : (
            <Tooltip title={"Received Access Request"}>
              <CallReceivedIcon sx={{ backgroundColor: "#26890D" }} />
            </Tooltip>
          )}
        </ToggleButton>
      </Box>

      {/* <CustomDataGridTableN
          tableRowsData={tableRowsData}
          columns={COLUMNS}
          handleTableRowClick={handleTableRowClick}
          isMultiLine={true}
          tableHeading={
            isRequestSend ? `Sent Access Request` : `Received Access Request`
          }
        /> */}
      <Box
        sx={{
          height: "76vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        key={`powertrain-table`}
      >
        <Box sx={{ height: "100%", overflowX: "auto" }}>
          <CustomDataGridV2
            rows={tableRowsData} 
            page={page}
            isMultiLine={true}
            rowsPerPage={rowsPerPage}
            columns={COLUMNS}
            totalItems={projectRequestCount}
            showPagination={true}
          />
        </Box>
      </Box>

      {/* <Paper elevation={4} sx={styles.paginationPaper}>
          <CustomPagination
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            hasMore={hasMore}
          />
        </Paper> */}

      
        <CustomDialogBoxV2
            open={openApproveAccessDialog}
        onClose={handleCancelApproveAccess}
              title={"Approve Access"}
              successButtonName={"Ok"}
              rejectButtonName={"Cancel"}
              onClickSuccess={handleConfirmApproveAccess}
              onClickReject={handleCancelApproveAccess}
              
              onConfirm={handleConfirmApproveAccess}
              actionButtonStyle={"continuous"}
            >
              <Stack spacing={1.2}>
                <DialogContentText>
                  Are you sure you want to approve access?
                </DialogContentText>
                {/* <RadioGroup
                row
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={selectedApproveAccessData?.approvingAccess}
                onChange={(e) =>
                  setSelectedApproveAccessData((prev) => ({
                    ...prev,
                    approvingAccess: Number(e.target.value),
                  }))
                }
              >
                <FormControlLabel
                  value={100}
                  control={<Radio />}
                  label="Co-Owner"
                />
                <FormControlLabel
                  value={200}
                  control={<Radio />}
                  label="Editor"
                />
                <FormControlLabel
                  value={300}
                  control={<Radio />}
                  label="Viewer"
                />
          </RadioGroup> */}
           <CustomRadioButtonV3
                size="small"
                 label="Action Type"
                
                data={[
                  { value: 100, label: "Co-Owner" },
                  { value: 200, label: "Editor"},
                  { value: 300, label:"Viewer" },
                ]}
              value={selectedApproveAccessData?.approvingAccess}
                onChange={(e) =>
                  setSelectedApproveAccessData((prev) => ({
                    ...prev,
                    approvingAccess: Number(e.target.value),
                  }))
                }
                direction={"column"}
                gap={1.5}
              />
              </Stack>
            </CustomDialogBoxV2>
      

      <Dialog open={openForwardApproveAccessDialog}>
        <DialogTitle>Forward Access For Approval</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.2}>
            <Box>
              {[
                {
                  label: "Project Name",
                  data: selectedRowData?.project_details
                    ?.map((project) => project?.name)
                    .join(", "),
                },
                {
                  label: "Trade Mark",
                  data: selectedRowData?.project_details
                    ?.map((project) => project?.trade_mark)
                    .join(", "),
                },
                {
                  label: "Project Owner",
                  data: selectedRowData?.project_owner
                    ?.map((owner) => owner?.fullname)
                    .join(", "),
                },
              ].map(({ label, data }) => (
                <Box
                  key={label}
                  sx={{ mb: 1, display: "flex", alignItems: "center" }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {label}:
                  </Typography>
                  <Typography sx={{ ml: 1 }}>{data || "-"}</Typography>
                </Box>
              ))}
            </Box>

            {selectedRowData?.requested_page_access_details?.length > 0 && (
              <Accordion style={{ marginBottom: "10px" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Pages
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {selectedRowData.requested_page_access_details.map(
                      (page, index) => (
                        <Grid item xs={12} key={index}>
                          <Box display="flex" alignItems="center">
                            {page?.page_name || "-"}
                          </Box>
                        </Grid>
                      ),
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Render Requested Feature Access */}
            {selectedRowData?.requested_feature_access_details?.length > 0 && (
              <Accordion style={{ marginBottom: "10px" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Features
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Object.entries(
                    selectedRowData.requested_feature_access_details.reduce(
                      (acc, feature) => {
                        if (!acc[feature?.feature_name])
                          acc[feature?.feature_name] = [];
                        acc[feature?.feature_name].push(feature);
                        return acc;
                      },
                      {},
                    ),
                  ).map(([featureName, featureGroup], index) => (
                    <Accordion
                      key={index}
                      style={{ marginBottom: "8px", marginLeft: "8px" }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body1" gutterBottom>
                          {featureName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {featureGroup.map((detail, index1) => {
                            if (!detail || typeof detail !== "object")
                              return null;
                            return (
                              <Grid item xs={12} key={index1}>
                                <Box display="flex" alignItems="center">
                                  {detail?.description || "-"}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
            <Box>
              <CustomTextFieldN
                size={"small"}
                placeholder={"Reason to forward access*"}
                type={"text"}
                value={reasonToForwardAccess}
                setValue={setReasonToForwardAccess}
                // valueError={assemblyNoError}
                setValueError={() => null}
                variant={"standard"}
                txtFldStyle={styles.txtStyle}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            sx={{ ...appTheme.commonBtnStyle }}
            color="error"
            variant="contained"
            onClick={() => handleCancelForwardAccess()}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            sx={{ ...appTheme.commonBtnStyle }}
            color="error"
            variant="contained"
            onClick={() => handleForwardAccessClick()}
            startIcon={<CheckIcon />}
            disabled={reasonToForwardAccess?.length < 5}
          >
            Forward
          </Button>
        </DialogActions>
      </Dialog>
      {/* <CustomConfirmationDialog
        open={openRejectAccessDialog}
        onClose={handleCancelRejectAccess}
        dialogTitle={"Reject Access"}
        disableConfirmBtn={reasonToRejectAccess?.length < 5}
        dialogContent={
          <Stack spacing={1.2}>
            <DialogContentText>
              Are you sure you want to reject access?
            </DialogContentText>
            <Box>
              <CustomTextFieldN
                size={"small"}
                // label={"Reason*"}
                placeholder={"Reason to reject access*"}
                type={"text"}
                // fldType={"text"}
                value={reasonToRejectAccess}
                setValue={setReasonToRejectAccess}
                // valueError={assemblyNoError}
                setValueError={() => null}
                variant={"standard"}
                txtFldStyle={styles.txtStyle}
              />
            </Box>
          </Stack>
        }
        onConfirm={handleConfirmRejectAccess}
      /> */}
      <CustomDialogBoxV2
              open={openRejectAccessDialog}
        onClose={handleCancelRejectAccess}
              title={"Reject Access"}
              successButtonName={"Ok"}
              rejectButtonName={"Cancel"}
              onClickSuccess={handleConfirmRejectAccess}
              onClickReject={handleCancelRejectAccess}

              onConfirm={handleConfirmRejectAccess}
              actionButtonStyle={"continuous"}
            >
              <Stack spacing={1.2}>
                <DialogContentText>
                  Are you sure you want to reject access?
                </DialogContentText>
                <Box>
                  <CustomFormTextFieldV2
                    size={"small"}
                    placeholder={"Reason to reject access*"}
                    type={"text"}
                    value={reasonToRejectAccess}
                    setValue={setReasonToRejectAccess}
                    // valueError={assemblyNoError}
                      variant={"outlined"}
                    setValueError={() => null}
                    
                     txtFldStyle={{
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#000000", // default border → black
      },
      "&:hover fieldset": {
        borderColor: "#000000", // hover border → black
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000000", // focused border → black
      },
    },
  }}
                  />
                </Box>
              </Stack>
            </CustomDialogBoxV2>
    </Box>
  );
}

export default PendingRequestsTab;

// Styles
const styles = {
  txtBold: {
    fontWeight: "bold",
  },
  filterBtnContainer: {
    mt: 1.4,
    mb:1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1,
    width: "100%",
  },
  prTableContainer: {
    mt: 1.4,
    height: 470,
    width: "100%",
    mb: 8,
    border: "1px solid grey",
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "10px",
    border: "1px solid grey",
  },
  txtStyle: {
    width: "100%",
  },
};
